/**
 * Survey format selection and pagination of the "multiple" format.
 *
 * Both rules come straight from the brief (§4.2 and §4.3) and are shared by the
 * API (which derives the format when it builds a survey instance) and the web
 * app (which renders the screens).
 */
/**
 * Assessing yourself and assessing colleagues are separate questionnaires.
 *
 * They ask the same twelve behaviors against the same ladder, but one is
 * written in the second person about the reader and the other in the third
 * person about someone else. Mixing them put a leader's own name into a grid
 * of their peers, asked as if they were a stranger.
 */
export const SURVEY_KINDS = ['SELF', 'OTHERS'];
/** A rater assigned exactly one person answers the single-person format. */
export const SURVEY_FORMATS = ['INDIVIDUAL', 'MULTIPLE'];
/**
 * Maximum number of evaluated people shown on one screen of the multiple
 * format. Above this the anchors and the people no longer fit together without
 * uncomfortable scrolling, which is the whole point of the grid layout.
 */
export const MAX_PEOPLE_PER_SCREEN = 6;
export const formatForAssignmentCount = (assignedCount) => assignedCount >= 2 ? 'MULTIPLE' : 'INDIVIDUAL';
/**
 * Split `total` people into balanced groups of at most MAX_PEOPLE_PER_SCREEN,
 * largest group first.
 *
 *   6  -> [6]          7  -> [4, 3]      8  -> [4, 4]
 *   9  -> [5, 4]       10 -> [5, 5]      11 -> [6, 5]
 *   12 -> [6, 6]       13 -> [5, 4, 4]
 *
 * Balanced rather than greedy: 13 people read better as 5+4+4 than as 6+6+1.
 */
export const balancedGroupSizes = (total) => {
    if (total <= 0)
        return [];
    const groupCount = Math.max(1, Math.ceil(total / MAX_PEOPLE_PER_SCREEN));
    const base = Math.floor(total / groupCount);
    const remainder = total % groupCount;
    return Array.from({ length: groupCount }, (_, index) => index < remainder ? base + 1 : base);
};
/** Start index of every group, so a screen can slice its own people. */
export const groupOffsets = (sizes) => sizes.reduce((offsets, size) => [...offsets, offsets[offsets.length - 1] + size], [0]);
/** Slice a list of people into the screens the multiple format will render. */
export const paginatePeople = (people) => {
    const offsets = groupOffsets(balancedGroupSizes(people.length));
    return offsets.slice(0, -1).map((start, index) => people.slice(start, offsets[index + 1]));
};
/**
 * Order in which a rater is asked about the people assigned to them.
 *
 * Closest working relationship first: the people they manage, then their own
 * manager, then peers, then stakeholders. Someone answering about eleven people
 * should move through them in an order that matches how they think about them,
 * not alphabetically across four different kinds of relationship.
 */
export const RELATIONSHIP_ORDER = [
    'SUPERVISOR',
    'DIRECT_REPORT',
    'PEER',
    'STAKEHOLDER',
];
/**
 * Split people into screens that never mix role levels.
 *
 * The anchors a rater reads belong to the *evaluated* person's role level — an
 * Area Leader and a People Leader are described differently on the same
 * behavior. A screen showing both could only display one set of anchors, so
 * people are partitioned by level first and the balanced grouping is applied
 * inside each cohort. The largest cohort is asked about first.
 *
 * The cost is occasionally one extra screen: 9 Area Leaders and 2 People
 * Leaders become [5, 4] + [2] rather than [6, 5]. Correct anchors are worth it.
 */
export const paginateByRoleLevel = (people) => {
    const cohorts = new Map();
    for (const person of people) {
        cohorts.set(person.roleLevel, [...(cohorts.get(person.roleLevel) ?? []), person]);
    }
    return [...cohorts.values()]
        .sort((a, b) => b.length - a.length)
        .flatMap((cohort) => paginatePeople(cohort));
};
/**
 * Build the screens of the multiple format.
 *
 * Two groupings, in this order:
 *
 *  1. **Role level**, because the anchors shown belong to the evaluated
 *     person's level. This one is not negotiable — a mixed screen could only
 *     show one set of anchors, and the other people would be assessed against
 *     the wrong text. Role level is the outer grouping so the anchors change as
 *     rarely as possible.
 *  2. **Relationship**, so the people a rater manages arrive together, then
 *     their own manager, then peers, then stakeholders.
 *
 * Within each block the balanced rule applies: at most six per screen, largest
 * group first. Larger cohorts are asked about first.
 */
export const buildSurveyScreens = (people) => {
    const byLevel = new Map();
    for (const person of people) {
        byLevel.set(person.roleLevel, [...(byLevel.get(person.roleLevel) ?? []), person]);
    }
    const rank = (relationship) => {
        const index = RELATIONSHIP_ORDER.indexOf(relationship);
        return index === -1 ? RELATIONSHIP_ORDER.length : index;
    };
    return [...byLevel.entries()]
        .sort(([, a], [, b]) => b.length - a.length)
        .flatMap(([roleLevel, cohort]) => {
        const byRelationship = new Map();
        for (const person of cohort) {
            byRelationship.set(person.relationship, [
                ...(byRelationship.get(person.relationship) ?? []),
                person,
            ]);
        }
        return [...byRelationship.entries()]
            .sort(([a], [b]) => rank(a) - rank(b))
            .flatMap(([relationship, group]) => paginatePeople(group).map((screenPeople) => ({
            people: screenPeople,
            roleLevel,
            relationship,
        })));
    });
};
/**
 * Rough time the questionnaire will take, in minutes.
 *
 * Reading a behavior and its three anchors costs about 25 seconds and happens
 * once per screen; placing each person on the scale costs about 8 seconds after
 * that. The open question at the end is optional, so it is costed at a minute
 * per person rather than at what writing a considered paragraph really takes —
 * quoting the maximum would talk people out of starting. Rounded up to the
 * nearest five so it reads as an estimate rather than a promise, and never
 * below five.
 *
 * The point is not precision — it is letting someone decide whether they have
 * time now or should come back later, which they can, since progress is saved.
 */
export const estimateMinutes = (behaviorCount, peopleCount) => {
    const people = Math.max(peopleCount, 1);
    const seconds = behaviorCount * 25 + behaviorCount * people * 8 + people * 60;
    return Math.max(5, Math.ceil(seconds / 60 / 5) * 5);
};
/**
 * Total number of questions a rater has to answer: every behavior for every
 * person assigned to them. Used for progress bars and for "surveys pending".
 */
export const totalQuestions = (assignedCount, behaviorCount) => assignedCount * behaviorCount;
//# sourceMappingURL=survey.js.map